import styled, { css } from "styled-components";
import { borderRadius, rawColors } from "@magicbi/ui-library";
import { spacing } from "../../../design-system/tokens/spacing";
import { duration, easing } from "../../../design-system/tokens/transitions";
import { borderWidth } from "../../../design-system/tokens/borders";
import type { IconButtonSize, IconButtonVariant } from "./IconButton.types";

const SIZE_MAP: Record<IconButtonSize, { btn: number; icon: number }> = {
  sm: { btn: 26, icon: 14 },
  md: { btn: 32, icon: 16 },
  lg: { btn: 38, icon: 18 },
  /** Canvas action bar nav item: 40×40, 20px icon (groups with smaller icons) */
  xl: { btn: 40, icon: 20 },
};

/** GAP: no token for the 3px keyboard-focus ring of the design's icon states. */
const RAIL_FOCUS_RING = "3px";

/** Rail look (design F23 "Tool bar and right rail icons"): every state is drawn here, so it replaces the default colours. */
const railVariant = css<{ $active?: boolean; $placing?: boolean }>`
  position: relative;
  border-radius: ${borderRadius[10]};
  background: ${({ $active, $placing }) =>
    $placing ? rawColors.secondary[100] : $active ? rawColors.primary[100] : "transparent"};
  color: ${({ $active, $placing }) =>
    $placing ? rawColors.secondary[700] : $active ? rawColors.text.interactive : rawColors.text.highEmphasis};
  box-shadow: ${({ $placing }) => ($placing ? `inset 0 0 0 ${borderWidth[2]} ${rawColors.secondary[300]}` : "none")};
  transition: background-color ${duration.fast} ${easing.smooth}, color ${duration.fast} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  /* Edge bar: the panel this button opens is open. */
  &::before {
    content: "";
    display: ${({ $active, $placing }) => ($active && !$placing ? "block" : "none")};
    position: absolute;
    left: -${spacing[2]};
    top: 50%;
    width: ${spacing[1]};
    height: ${spacing[5]};
    transform: translateY(-50%);
    border-radius: ${borderRadius.full};
    background: ${rawColors.primary[500]};
  }

  &:hover:not(:disabled):not([aria-disabled="true"]) {
    background: ${({ $active, $placing }) =>
      $placing ? rawColors.secondary[100] : $active ? rawColors.primary[100] : rawColors.grey[300]};
    color: ${({ $active, $placing }) =>
      $placing ? rawColors.secondary[700] : $active ? rawColors.text.interactive : rawColors.text.highEmphasis};
  }

  &:active:not(:disabled):not([aria-disabled="true"]) {
    background: ${({ $placing }) => ($placing ? rawColors.secondary[100] : rawColors.primary[100])};
    color: ${({ $placing }) => ($placing ? rawColors.secondary[700] : rawColors.text.interactive)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 ${RAIL_FOCUS_RING} color-mix(in srgb, ${rawColors.primary[500]} 32%, transparent);
  }

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 1;
    background: transparent;
    color: ${rawColors.grey[400]};
    box-shadow: none;
    cursor: not-allowed;
  }

  &:disabled:hover,
  &[aria-disabled="true"]:hover {
    background: ${rawColors.structural[50]};
  }
`;

const VARIANT_STYLES: Record<IconButtonVariant, ReturnType<typeof css> | null> = {
  default: null,
  rail: railVariant,
};

export const Button = styled.button<{
  $size: IconButtonSize;
  $variant?: IconButtonVariant;
  $active?: boolean;
  $tone?: "default" | "primary";
  $ring?: boolean;
  $bordered?: boolean;
  $placing?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({ $size }) => SIZE_MAP[$size].btn}px;
  height: ${({ $size }) => SIZE_MAP[$size].btn}px;
  box-sizing: border-box;
  border: ${({ $ring, $bordered }) =>
    $ring
      ? `1px solid ${rawColors.brand.primary}`
      : $bordered
        ? `1px solid ${rawColors.stroke[200]}`
        : "none"};
  border-radius: ${borderRadius.md};
  background: ${({ $active }) =>
    $active ? rawColors.primary[50] : "transparent"};
  color: ${({ $active, $tone }) =>
    $active
      ? rawColors.primary[500]
      : $tone === "primary"
        ? rawColors.brand.primary
        : rawColors.text.highEmphasis};
  cursor: pointer;
  transition: background-color ${duration.fast} ${easing.smooth}, color ${duration.fast} ${easing.smooth};
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  &:hover:not(:disabled):not([aria-disabled="true"]) {
    background: ${({ $active }) =>
      $active ? rawColors.primary[100] : rawColors.grey[50]};
    color: ${({ $active }) =>
      $active ? rawColors.primary[600] : rawColors.text.highEmphasis};
  }

  &:active:not(:disabled):not([aria-disabled="true"]) {
    background: ${rawColors.primary[100]};
    color: ${rawColors.primary[700]};
  }

  &:disabled,
  &[aria-disabled="true"] {
    opacity: 0.38;
    cursor: not-allowed;
  }

  ${({ $variant = "default" }) => VARIANT_STYLES[$variant]}

  /* Expose icon size to child */
  --icon-size: ${({ $size }) => SIZE_MAP[$size].icon}px;
`;

export { SIZE_MAP };
